import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tabs,
  Tab,
  LinearProgress,
  TablePagination,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Download,
  TrendingUp,
  BarChart as BarChartIcon,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

import api from "../../api/axiosInstance";
import invoiceService from "../../services/invoiceService";
import PageHeader from "../../components/common/PageHeader";
import KpiCard from "../../components/common/KpiCard";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import { formatCi, formatUsd } from "../../utils/currency";
import useUrlState from "../../hooks/useUrlState";
import { slugifyTab } from "../../utils/tabSlug";

const TAB_LABELS = ["Revenue", "Top Customers", "Top Vendors", "Profit & Loss", "House Accounts"];
const ADMIN_REPORTS_URL_SCHEMA = {
  tab: { default: "" },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 1.5,
        boxShadow: 3,
      }}
    >
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
        {label}
      </Typography>
      {payload.map((p) => (
        <Typography
          key={p.name}
          variant="caption"
          display="block"
          sx={{ color: p.color }}
        >
          {p.name}:{" "}
          {typeof p.value === "number" &&
          (p.name === "Revenue" || p.name === "Collected")
            ? formatCi(p.value)
            : p.value}
        </Typography>
      ))}
    </Box>
  );
};

// Renders one Profit & Loss dataset (KPIs + cost breakdown + by-shipment/
// by-item tables) — shared by the main P&L tab and the House Accounts tab,
// which are the same report run with a different `scope` (see
// reportController.getProfitLoss). Both arrive and stay in CI$.
// Renders a signed amount as a "profit/loss" pair instead of one signed
// number — e.g. CI$100.00 of profit shows as "CI$100.00 / 0", a CI$100.00
// loss shows as "0 / CI$100.00" in red. Exactly one side of the pair is
// ever nonzero.
const ProfitLossPair = ({ value }) => {
  const v = value || 0;
  const isLoss = v < 0;
  return (
    <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: isLoss ? "error.main" : "success.main" }}>
      {isLoss ? `0 / ${formatCi(Math.abs(v))}` : `${formatCi(v)} / 0`}
    </Typography>
  );
};

const ProfitLossPanel = ({ data, loading, emptyMessage, page, limit, onPageChange, onLimitChange }) => {
  // Full-panel skeleton only on the very first load (no data yet at all) —
  // a page/rows-per-page change on the By Sailing table re-triggers this
  // same `loading` flag, but by then `data` already exists, so the whole
  // panel (KPI cards included) just stays put and only the table dims
  // slightly below instead of the entire tab flashing back to a skeleton.
  if (loading && !data) return <SkeletonLoader rows={5} />;
  if (!data || !(data.pagination?.total ?? data.sailings?.length)) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
        {emptyMessage}
      </Typography>
    );
  }

  const b = data.totals.costBreakdown || {};
  // Regrouped to mirror the three Expenses page tabs instead of the raw
  // internal cost components: Freight/Shipping = Freight + every MBL Fees
  // line except "Duties" (that one moves into Duties below); Duties = the
  // MBL "Duties" line + HBOL Fees (each HBOL's own fees, including any
  // ad-hoc lines an admin added there); Port Fees = D/R Cargo + Trucking
  // charges. All three still sum to Total Cost — no invoice-level pass-
  // through fields are part of Cost at all (see reportController.getProfitLoss).
  const freightShipping = (b.freight || 0) + (b.mblFees || 0) - (b.mblDuty || 0);
  const duties = (b.mblDuty || 0) + (b.hbolFees || 0);
  const portFees = b.drPortFees || 0;

  // Overall net Profit/Loss (Revenue − Cost, computed server-side from the
  // FULL dataset before pagination) — NOT a sum of only the sailings
  // running positive or only the ones running negative, which would show
  // a "Profit" and a "Loss" card both nonzero at once and neither matching
  // the business's actual bottom line. Exactly one of these two cards is
  // nonzero at any given time. lossSailingsCount (how many individual
  // sailings are underwater) is still shown underneath either way, since
  // that's useful context regardless of which way the overall net goes.
  const netProfit = data.totals.profit || 0;
  const profitTotal = netProfit >= 0 ? netProfit : 0;
  const lossTotal = netProfit < 0 ? Math.abs(netProfit) : 0;
  const lossSailingsCount = data.totals.lossSailingsCount || 0;

  return (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, p: 2.5, pb: 0 }}>
        <KpiCard
          label="Total Cost"
          value={formatCi(data.totals.cost)}
          icon={<BarChartIcon />}
          iconBg="#FFF5F5"
          iconColor="#C53030"
        />
        <KpiCard
          label="Freight / Shipping"
          value={formatCi(freightShipping)}
          icon={<BarChartIcon />}
          iconBg="#EBF4FF"
          iconColor="#2B6CB0"
        />
        <KpiCard
          label="Duties"
          value={formatCi(duties)}
          icon={<BarChartIcon />}
          iconBg="#FFFBEB"
          iconColor="#C05621"
        />
        <KpiCard
          label="Port Fees"
          value={formatCi(portFees)}
          icon={<BarChartIcon />}
          iconBg="#F5F3FF"
          iconColor="#6D28D9"
        />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, p: 2.5, pt: 2 }}>
        <KpiCard
          label="Profit"
          value={formatCi(profitTotal)}
          delta={netProfit >= 0 ? "Overall net across every sailing" : "Overall net is negative this period"}
          deltaType="up"
          icon={<TrendingUp />}
          iconBg="#ECFDF5"
          iconColor="#0E9F6E"
        />
        <KpiCard
          label="Loss"
          value={formatCi(lossTotal)}
          delta={lossSailingsCount > 0 ? `${lossSailingsCount} sailing(s) running a loss` : "No sailings at a loss"}
          deltaType={lossSailingsCount > 0 ? "down" : "neutral"}
          icon={<BarChartIcon />}
          iconBg={lossSailingsCount > 0 ? "#FFF5F5" : "#ECFDF5"}
          iconColor={lossSailingsCount > 0 ? "#C53030" : "#0E9F6E"}
        />
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ px: 2.5, pt: 1, display: "block" }}>
        All figures shown in CI$ — the currency these invoices and fees are actually billed in.
        Total Cost = Freight/Shipping (split across a sailing's whole roster by weight, regardless of
        customer type) + Duties (sailing-wide Duties line + every HBOL's own fees) + Port Fees (split by
        piece count across items sharing that Dock Receipt) — cost-of-goods still isn't tracked, and
        invoice-level duty/port-fee pass-throughs are a billing split against Revenue, not a cost.
        Profit/Loss above is the single overall net (Revenue − Cost) across every sailing — never both
        nonzero at once — with the sub-label showing how many individual sailings are running a loss even
        when the business overall is still profitable.
      </Typography>

      <Box sx={{ overflowX: "auto", p: 2.5, opacity: loading ? 0.5 : 1, transition: "opacity 0.15s", pointerEvents: loading ? "none" : "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sailing</TableCell>
              <TableCell>MBL #</TableCell>
              <TableCell align="right">House Account</TableCell>
              <TableCell align="right">Revenue</TableCell>
              <TableCell align="right">Pending</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell align="right">
                Profit/Loss
                <br />
                <Typography component="span" sx={{ fontSize: 10, fontWeight: 400 }} color="text.secondary">
                  (excl. House Account)
                </Typography>
              </TableCell>
              <TableCell align="right">Profit/Loss</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.sailings || []).map((s) => (
              <TableRow key={s.sailingId}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {s.sailingId === "unassigned"
                      ? "Not yet on a sailing"
                      : `${s.sailingNumber || "—"} — ${s.vesselName || ""}${s.voyageNumber ? ` / ${s.voyageNumber}` : ""}`}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{s.masterBillNumber || "—"}</TableCell>
                <TableCell align="right" sx={{ color: "text.secondary" }}>
                  {formatCi(s.houseAccountCost)}
                </TableCell>
                <TableCell align="right">{formatCi(s.revenue)}</TableCell>
                <TableCell align="right" sx={{ color: "warning.main" }}>
                  {formatCi(s.pending)}
                </TableCell>
                <TableCell align="right" sx={{ color: "error.main" }}>
                  {formatCi(s.cost)}
                </TableCell>
                <TableCell align="right">
                  <ProfitLossPair value={s.profitExclHouse} />
                </TableCell>
                <TableCell align="right">
                  <ProfitLossPair value={s.profit} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data.pagination?.total ?? (data.sailings || []).length}
          page={page}
          onPageChange={(_, p) => onPageChange(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => onLimitChange(+e.target.value)}
          rowsPerPageOptions={[3, 5, 10]}
        />
      </Box>
    </>
  );
};

const AdminReports = () => {
  const theme = useTheme();
  const [{ tab }, setUrlState] = useUrlState(ADMIN_REPORTS_URL_SCHEMA);
  const activeTab = Math.max(0, TAB_LABELS.findIndex((label) => slugifyTab(label) === tab));
  const [months, setMonths] = useState("7");
  const [loading, setLoading] = useState(true);

  // Shown entirely in CI$ — the currency these figures are actually billed
  // in — with no US$ conversion anywhere on this page. A display-time
  // CI$→US$ conversion (as this page used to do) moves with the exchange
  // rate even though nothing underlying changed, which made every figure
  // here look like it was drifting whenever the admin-configured rate was
  // updated. Showing the real billed currency avoids that entirely.
  const [revenueData, setRevenueData] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [shipmentStats, setShipmentStats] = useState(null);
  const [finSummary, setFinSummary] = useState(null);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topCustomersLoading, setTopCustomersLoading] = useState(true);
  const [topVendors, setTopVendors] = useState([]);
  const [topVendorsLoading, setTopVendorsLoading] = useState(true);

  const [plLoading, setPlLoading] = useState(true);
  const [plData, setPlData] = useState(null);
  const [plPage, setPlPage] = useState(0);
  const [plLimit, setPlLimit] = useState(5); // 3 | 5 | 10 — By Sailing table only

  // Same report, `scope=house` — customers flagged as House Accounts (see
  // AdminCustomers.jsx), whose invoices are auto-settled and excluded from
  // the normal P&L above. Surfaced on its own tab instead.
  const [houseLoading, setHouseLoading] = useState(true);
  const [houseData, setHouseData] = useState(null);
  const [housePage, setHousePage] = useState(0);
  const [houseLimit, setHouseLimit] = useState(5);

  // Header KPIs (Total Shipments / Collected / Outstanding / Collection
  // Rate) are shown above the tabs on every tab, so they load once on
  // mount regardless of which tab is active — everything else below is
  // gated to its own tab and only fetches when that tab is actually open.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [statsRes, finRes] = await Promise.all([api.get("/shipments/stats"), invoiceService.getSummary()]);
        setShipmentStats(statsRes.data.data);
        setFinSummary(finRes.data.data);
      } catch (e) {
        console.error("Reports load error:", e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (activeTab !== 0) return;
    (async () => {
      setRevenueLoading(true);
      try {
        const { data } = await api.get("/reports/revenue", { params: { months } });
        setRevenueData(data.data?.revenue || []);
      } catch (e) {
        console.error("Revenue load error:", e.message);
      } finally {
        setRevenueLoading(false);
      }
    })();
  }, [activeTab, months]);

  useEffect(() => {
    if (activeTab !== 1) return;
    (async () => {
      setTopCustomersLoading(true);
      try {
        const { data } = await api.get("/reports/top-customers", { params: { limit: 5 } });
        setTopCustomers(data.data?.customers || []);
      } catch (e) {
        console.error("Top customers load error:", e.message);
      } finally {
        setTopCustomersLoading(false);
      }
    })();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 2) return;
    (async () => {
      setTopVendorsLoading(true);
      try {
        const { data } = await api.get("/reports/top-vendors", { params: { limit: 5 } });
        setTopVendors(data.data?.vendors || []);
      } catch (e) {
        console.error("Top vendors load error:", e.message);
      } finally {
        setTopVendorsLoading(false);
      }
    })();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 3) return;
    (async () => {
      setPlLoading(true);
      try {
        const { data } = await api.get("/reports/profit-loss", { params: { page: plPage, limit: plLimit } });
        setPlData(data.data);
      } catch (e) {
        console.error("Profit/loss load error:", e.message);
      } finally {
        setPlLoading(false);
      }
    })();
  }, [activeTab, plPage, plLimit]);

  useEffect(() => {
    if (activeTab !== 4) return;
    (async () => {
      setHouseLoading(true);
      try {
        const { data } = await api.get("/reports/profit-loss", {
          params: { scope: "house", page: housePage, limit: houseLimit },
        });
        setHouseData(data.data);
      } catch (e) {
        console.error("House accounts P&L load error:", e.message);
      } finally {
        setHouseLoading(false);
      }
    })();
  }, [activeTab, housePage, houseLimit]);

  const totalShipments = shipmentStats?.totalShipments || 0;
  // `|| 1` as a divide-by-zero guard would also kick in whenever outstanding
  // is legitimately 0 (everything collected) — silently reporting 99.x%
  // instead of 100%. Guard the whole denominator instead, only when both
  // are actually zero.
  const collectionRate = (() => {
    if (!finSummary) return 0;
    const totalCollected = finSummary.totalCollected || 0;
    const outstanding = finSummary.outstanding || 0;
    const denom = totalCollected + outstanding;
    return denom > 0 ? Math.round((totalCollected / denom) * 100) : 0;
  })();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Reports & Analytics"
        subtitle="Business performance overview"
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              select
              size="small"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              sx={{ width: 150 }}
            >
              <MenuItem value="3">Last 3 months</MenuItem>
              <MenuItem value="6">Last 6 months</MenuItem>
              <MenuItem value="7">Last 7 months</MenuItem>
              <MenuItem value="12">Last 12 months</MenuItem>
            </TextField>
            <Button variant="outlined" size="small" startIcon={<Download />}>
              Export CSV
            </Button>
            <Button variant="outlined" size="small" startIcon={<Download />}>
              Export PDF
            </Button>
          </Box>
        }
      />

      {loading ? <SkeletonLoader rows={2} /> : null}

      {/* KPIs */}
      <Box
        sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}
      >
        <KpiCard
          label="Total Shipments"
          value={loading ? "—" : totalShipments.toString()}
          icon={<TrendingUp />}
          iconBg="#EBF4FF"
          iconColor="#2B6CB0"
        />
        <KpiCard
          label="Total Collected"
          value={
            loading ? "—" : formatCi(finSummary?.totalCollected)
          }
          deltaType="up"
          icon={<TrendingUp />}
          iconBg="#ECFDF5"
          iconColor="#0E9F6E"
        />
        <KpiCard
          label="Outstanding"
          value={
            loading
              ? "—"
              : formatCi(finSummary?.outstanding)
          }
          deltaType="down"
          icon={<BarChartIcon />}
          iconBg="#FFF5F5"
          iconColor="#C53030"
        />
        <KpiCard
          label="Collection Rate"
          value={loading ? "—" : `${collectionRate}%`}
          icon={<BarChartIcon />}
          iconBg="#E9D8FD"
          iconColor="#553C9A"
        />
      </Box>

      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setUrlState({ tab: slugifyTab(TAB_LABELS[v]) })}
          sx={{
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              fontSize: 13,
              textTransform: "none",
              fontWeight: 600,
              minHeight: 44,
            },
          }}
        >
          {TAB_LABELS.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>

        {/* Revenue */}
        {activeTab === 0 && (
          <CardContent>
            {revenueLoading ? (
              <SkeletonLoader rows={5} />
            ) : revenueData.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 4, textAlign: "center" }}
              >
                No revenue data yet. Seed the database and create invoices.
              </Typography>
            ) : (
              <>
                <Typography fontWeight={700} mb={2}>
                  Revenue vs Collections
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={revenueData} barSize={16}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={theme.palette.divider}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11, fill: theme.palette.text.disabled }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={(v) => `CI$${(v / 1000).toFixed(0)}k`}
                      tick={{ fontSize: 11, fill: theme.palette.text.disabled }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: theme.palette.action.hover }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar
                      dataKey="revenue"
                      name="Revenue"
                      fill="#0D629B"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="collected"
                      name="Collected"
                      fill="#C6F6D5"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>

                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Revenue = invoiced that month. Collected = payments received that month — not necessarily against
                  that same month's invoices, so "Net" below is a cash-flow gap for the month, not a running
                  outstanding balance (see the Total Outstanding KPI above / the Invoices page for that).
                </Typography>

                <Box sx={{ mt: 2, overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Month</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        <TableCell align="right">Collected</TableCell>
                        <TableCell align="right">Net</TableCell>
                        <TableCell align="right">Shipments</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...revenueData].reverse().map((row) => {
                        const net = (row.revenue || 0) - (row.collected || 0);
                        return (
                          <TableRow key={row.month}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {row.month}
                            </TableCell>
                            <TableCell align="right">
                              {formatCi(row.revenue)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: "success.main", fontWeight: 600 }}
                            >
                              {formatCi(row.collected)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: net > 0 ? "error.main" : "success.main" }}
                            >
                              {net < 0 ? "-" : ""}{formatCi(Math.abs(net))}
                            </TableCell>
                            <TableCell align="right">
                              {row.shipments || 0}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </CardContent>
        )}

        {/* Top Customers */}
        {activeTab === 1 && (
          <Box sx={{ overflowX: "auto" }}>
            {topCustomersLoading ? (
              <SkeletonLoader rows={5} />
            ) : topCustomers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                No data yet.
              </Typography>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="center">Shipments</TableCell>
                    <TableCell align="right">Invoice Total (US$)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topCustomers.map((c, i) => (
                    <TableRow key={c._id}>
                      <TableCell
                        sx={{ color: "text.disabled", fontWeight: 700 }}
                      >
                        {i + 1}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {c.firstName} {c.lastName}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                        {c.email}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        {c.count}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      >
                        US{formatUsd(c.totalValue)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {/* Top Vendors */}
        {activeTab === 2 && (
          <Box sx={{ overflowX: "auto" }}>
            {topVendorsLoading ? (
              <SkeletonLoader rows={5} />
            ) : topVendors.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                No data yet.
              </Typography>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Vendor / Store</TableCell>
                    <TableCell align="center">Shipments</TableCell>
                    <TableCell align="right">Invoice Total (US$)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topVendors.map((v, i) => (
                    <TableRow key={v.vendor}>
                      <TableCell
                        sx={{ color: "text.disabled", fontWeight: 700 }}
                      >
                        {i + 1}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{v.vendor}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        {v.count}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      >
                        US{formatUsd(v.totalValue)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {/* Profit & Loss */}
        {activeTab === 3 && (
          <Box>
            <ProfitLossPanel
              data={plData}
              loading={plLoading}
              page={plPage}
              limit={plLimit}
              onPageChange={setPlPage}
              onLimitChange={(l) => {
                setPlLimit(l);
                setPlPage(0);
              }}
              emptyMessage="No invoiced items yet — profit/loss is only computed for items that have been billed at least once."
            />
          </Box>
        )}

        {/* House Accounts — same report, scoped to customers flagged as
            House Account in Customer Management (auto-settled invoices,
            excluded from the Profit & Loss tab above). */}
        {activeTab === 4 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2.5, pt: 2, display: "block" }}>
              Customers flagged as "House Account" — their invoices are auto-marked paid and excluded from every
              other financial report on this page. Toggle the flag from Customer Management.
            </Typography>
            <ProfitLossPanel
              data={houseData}
              loading={houseLoading}
              page={housePage}
              limit={houseLimit}
              onPageChange={setHousePage}
              onLimitChange={(l) => {
                setHouseLimit(l);
                setHousePage(0);
              }}
              emptyMessage="No house-account customers yet — flag one from Customer Management to see their activity here."
            />
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default AdminReports;
